import { useEffect, useMemo, useState, type FormEvent } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import MarketingLanguageToggle from '../components/marketing/MarketingLanguageToggle';
import MarketingThemeToggle from '../components/marketing/MarketingThemeToggle';
import { useT } from '../i18n/useT';
import type { StringKey } from '../i18n/strings';
import { applyLangFromUrl } from '../marketing/marketingLangUrl';
import { useMarketingPageMeta } from '../marketing/useMarketingPageMeta';
import {
  fetchWaitingListStanding,
  joinWaitingList,
  type WaitingListMembership,
  type StandingBucket,
} from '../services/waitingListApi';
import './landing.css';
import './guestSignup.css';
import './waitingList.css';

const ASSETS = `${process.env.PUBLIC_URL || ''}/marketing-assets`;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BUCKET_LABEL_KEY: Record<StandingBucket, StringKey> = {
  top_1: 'waitlistBucketTop1',
  top_10: 'waitlistBucketTop10',
  top_50: 'waitlistBucketTop50',
  in_list: 'waitlistBucketInList',
};

const BUCKET_HINT_KEY: Record<StandingBucket, StringKey> = {
  top_1: 'waitlistHintTop1',
  top_10: 'waitlistHintTop10',
  top_50: 'waitlistHintTop50',
  in_list: 'waitlistHintInList',
};

function StandingCard({
  membership,
  onCopied,
}: {
  membership: WaitingListMembership;
  onCopied: (ok: boolean) => void;
}) {
  const t = useT();
  const bucket = membership.standing.bucket;
  const refs = membership.standing.referral_count;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(membership.referral_link);
      onCopied(true);
    } catch {
      onCopied(false);
    }
  };

  const shareNative = async () => {
    if (typeof navigator.share !== 'function') {
      await copyLink();
      return;
    }
    try {
      await navigator.share({
        title: t('waitlistShareNativeTitle'),
        text: t('waitlistShareNativeText'),
        url: membership.referral_link,
      });
    } catch {
      /* cancelled */
    }
  };

  return (
    <section className="wl-standing" aria-live="polite">
      <p className="wl-standing-kicker">
        {membership.already_joined ? t('waitlistAlreadyKicker') : t('waitlistWelcomeKicker')}
      </p>

      <div className={`wl-badge wl-badge--${bucket}`}>
        <div className="wl-badge-icon" aria-hidden="true">
          <EmojiEventsOutlinedIcon sx={{ fontSize: 22 }} />
        </div>
        <div className="wl-badge-copy">
          <span className="wl-badge-label">{t(BUCKET_LABEL_KEY[bucket])}</span>
          <span className="wl-badge-meta">{t(BUCKET_HINT_KEY[bucket])}</span>
        </div>
      </div>

      <ol className="wl-meter" aria-hidden="true">
        {(['top_1', 'top_10', 'top_50', 'in_list'] as StandingBucket[]).map((tier) => (
          <li
            key={tier}
            className={`wl-meter-step${bucket === tier ? ' is-active' : ''}${
              rankIndex(bucket) <= rankIndex(tier) ? ' is-reached' : ''
            }`}
          />
        ))}
      </ol>

      {refs > 0 && (
        <p className="wl-standing-refs">
          {t(refs === 1 ? 'waitlistReferralCountOne' : 'waitlistReferralCountMany', { n: refs })}
        </p>
      )}

      <div className="wl-share">
        <p className="wl-share-title">{t('waitlistShareTitle')}</p>
        <p className="wl-share-lead">{t('waitlistShareLead')}</p>
        <div className="wl-share-row">
          <input
            className="gs-input wl-share-input"
            readOnly
            value={membership.referral_link}
            aria-label={t('waitlistShareInputAria')}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" className="wl-share-btn" onClick={copyLink}>
            <ContentCopyIcon sx={{ fontSize: 18 }} />
            {t('waitlistCopy')}
          </button>
        </div>
        <button type="button" className="wl-share-native" onClick={shareNative}>
          <IosShareIcon sx={{ fontSize: 18 }} />
          {t('waitlistShare')}
        </button>
        <p className="wl-share-code">
          {t('waitlistCodePrefix')} <strong>{membership.referral_code}</strong>
        </p>
      </div>
    </section>
  );
}

function rankIndex(bucket: StandingBucket): number {
  switch (bucket) {
    case 'top_1':
      return 0;
    case 'top_10':
      return 1;
    case 'top_50':
      return 2;
    default:
      return 3;
  }
}

function HowItWorks() {
  const t = useT();
  return (
    <div className="wl-how">
      <p className="wl-how-title">{t('waitlistHowTitle')}</p>
      <ol className="wl-how-list">
        <li>{t('waitlistHowStep1')}</li>
        <li>{t('waitlistHowStep2')}</li>
        <li>{t('waitlistHowStep3')}</li>
      </ol>
    </div>
  );
}

export default function WaitingListPage() {
  const t = useT();
  useMarketingPageMeta(t('waitlistMetaTitle'), t('waitlistMetaDescription'), '/waiting-list');

  const [searchParams] = useSearchParams();
  useEffect(() => {
    applyLangFromUrl(window.location.search);
  }, [searchParams]);
  const referralFromUrl = useMemo(
    () => (searchParams.get('ref') || '').trim().toUpperCase(),
    [searchParams],
  );

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copyNote, setCopyNote] = useState<string | null>(null);
  const [membership, setMembership] = useState<WaitingListMembership | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchWaitingListStanding();
      if (cancelled) return;
      if (result.ok) {
        setMembership(result.membership);
      }
      setIsRestoring(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const mapJoinError = (status: number, apiMessage?: string): string => {
    if (status === 422) return t('waitlistInvalidEmail');
    if (status === 404) return t('waitlistGenericError');
    if (apiMessage && /valid email/i.test(apiMessage)) return t('waitlistInvalidEmail');
    return t('waitlistGenericError');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setCopyNote(null);

    const trimmed = email.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setError(t('waitlistInvalidEmail'));
      return;
    }

    setIsLoading(true);
    try {
      const result = await joinWaitingList(trimmed, referralFromUrl || undefined);
      if (result.ok) {
        setMembership(result.membership);
        setEmail('');
        return;
      }
      setError(mapJoinError(result.status, result.message));
    } catch {
      setError(t('waitlistConnectionError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="gs-page wl-page">
      <div className="gs-grid">
        <aside className="gs-aside" aria-hidden="true">
          <div className="gs-aside-pin">
            <div className="gs-stage">
              <img
                src={`${ASSETS}/Hero.png`}
                alt=""
                className="wl-aside-img wl-aside-img--light"
              />
              <img
                src={`${ASSETS}/Hero-dark.png`}
                alt=""
                className="wl-aside-img wl-aside-img--dark"
              />
            </div>
          </div>
        </aside>

        <main className="gs-main">
          <header className="gs-bar">
            <RouterLink to="/landing" className="gs-back">
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              {t('waitlistBackHome')}
            </RouterLink>
            <div className="gs-tools">
              <MarketingLanguageToggle />
              <MarketingThemeToggle />
            </div>
          </header>

          <div className="gs-card">
            <RouterLink to="/landing" className="gs-logo" aria-label={t('brandName')}>
              <img src={`${ASSETS}/VisiMe_Logo_RGB-1.png`} alt="" />
              <span>{t('brandName')}</span>
            </RouterLink>
            <p className="l-eyebrow">{t('waitlistEyebrow')}</p>
            <h1 className="gs-title">{t('waitlistTitle')}</h1>
            <p className="gs-lead">{t('waitlistLead')}</p>

            {!membership && !isRestoring && <HowItWorks />}

            {referralFromUrl && !membership && (
              <p className="wl-ref-banner" role="status">
                {t('waitlistInviteActive', { code: referralFromUrl })}
              </p>
            )}

            {error && (
              <p className="gs-error" role="alert">
                {error}
              </p>
            )}
            {copyNote && (
              <p className="wl-success" role="status">
                {copyNote}
              </p>
            )}

            {isRestoring && !membership && (
              <div className="wl-restoring" role="status" aria-busy="true">
                <span className="wl-skeleton wl-skeleton--badge" />
                <span className="wl-skeleton wl-skeleton--line" />
                <span className="wl-skeleton wl-skeleton--line wl-skeleton--short" />
                <p className="wl-restoring-text">{t('waitlistRestoring')}</p>
              </div>
            )}

            {membership ? (
              <StandingCard
                membership={membership}
                onCopied={(ok) => setCopyNote(ok ? t('waitlistCopied') : t('waitlistCopyFailed'))}
              />
            ) : (
              !isRestoring && (
                <form className="gs-form" onSubmit={handleSubmit} noValidate>
                  <div className="gs-field">
                    <label htmlFor="wl-email">{t('waitlistEmailLabel')}</label>
                    <input
                      id="wl-email"
                      className="gs-input"
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                      placeholder={t('waitlistEmailPlaceholder')}
                    />
                  </div>
                  <button
                    type="submit"
                    className="gs-submit wl-submit"
                    disabled={isLoading}
                    aria-busy={isLoading}
                  >
                    {isLoading ? t('waitlistSubmitting') : t('waitlistSubmit')}
                  </button>
                </form>
              )
            )}

            <p className="gs-switch">
              {t('waitlistAlreadyAccount')}{' '}
              <RouterLink to="/?login=1" className="gs-link">
                {t('waitlistSignIn')}
              </RouterLink>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
